import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

export interface ScopeChartProps {
  scope1: number
  scope2: number
  scope3: number
}

export const ScopeChart = ({ scope1, scope2, scope3 }: ScopeChartProps) => {
  const { t } = useTranslation()

  const options = {
    indexAxis: 'y' as const,
    elements: {
      bar: {
        borderWidth: 1,
      },
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        position: 'left' as const,
        ticks: {
          crossAlign: 'far',
          color: 'black',
          font: {
            size: 14,
            family: 'sans-serif',
          },
          callback: function (value: number, index: number): string {
            // Get the full label from the chart data
            const labels = (this as any).chart.data.labels as string[]
            if (!labels || !labels[index]) return ''
            const category = labels[index].split(':')[0]
            return category.length > 25
              ? category.substring(0, 17) + '...'
              : category
          },
          maxRotation: 0,
          padding: 5,
          align: 'center',
        },
        grid: {
          display: false,
        },
        afterFit: function (scale: any) {
          scale.width = Math.max(scale.width, 150)
        },
      },
      y1: {
        position: 'right' as const,
        ticks: {
          crossAlign: 'far',
          color: 'black',
          font: {
            size: 14,
            family: 'sans-serif',
          },
          callback: function (value: number, index: number): string {
            // Get the full label from the chart data
            const labels = (this as any).chart.data.labels as string[]
            if (!labels || !labels[index]) return ''
            const label = labels[index]
            const percentMatch = label.match(/: (\d+)%/)
            return `${percentMatch?.[1]}%`
          },
        },
        grid: {
          display: false,
        },
      },
      x: {
        display: false,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    barThickness: 30,
  }

  // Calculate total for percentage
  const totalValue = scope1 + scope2 + scope3

  // Create and sort the data entries
  const sortedEntries = [
    { key: 'scope1', value: scope1, label: 'Scope 1' },
    { key: 'scope2', value: scope2, label: 'Scope 2' },
    { key: 'scope3', value: scope3, label: 'Scope 3' },
  ].map((entry) => ({
    ...entry,
    label: `${entry.label}: ${Math.round((entry.value / totalValue) * 100)}%`,
  }))

  const data = {
    labels: sortedEntries.map((entry) => entry.label),
    datasets: [
      {
        data: sortedEntries.map((entry) => entry.value),
        backgroundColor: Array(sortedEntries.length).fill(
          'rgba(59, 130, 246, 0.5)'
        ),
        borderRadius: 8,
        yAxisID: 'y',
      },
    ],
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-12">
      <div>
        <span className="truncate text-sm font-medium text-gray-500 w-full justify-between flex">
          Scopes
        </span>

        <div className="h-52">
          <Bar options={options as any} data={data} />
        </div>
      </div>
      <div>
        <ul className="pt-2">
          <li className="py-1">
            <span className="font-bold">Scope 1 </span>
            <span className="text-gray-700">
              {t('dashboard.charts.scope1')}
            </span>
          </li>
          <li className="py-1">
            <span className="font-bold">Scope 2 </span>
            <span className="text-gray-700">
              {t('dashboard.charts.scope2')}
            </span>
          </li>
          <li className="py-1">
            <span className="font-bold">Scope 3 Upstream </span>
            <span className="text-gray-700">
              {t('dashboard.charts.scope3')}
            </span>
          </li>
        </ul>
      </div>
    </div>
  )
}
